/**
 * "Senaste övningar" — the compact practice history on the setup page
 * (integration pass 3; `usePracticeHistory` previously had no UI).
 *
 * It answers one question before a student picks filters: *what did I just do,
 * and how did it go?* So each row is one line — topic, where the problem comes
 * from (`SourceBadge`, so a hand-in or a test-bank item is never mistaken for
 * an original), the outcome, when — plus a retry that re-serves that exact
 * problem as a one-question session rather than sending the student back
 * through the filter form.
 *
 * The list is deliberately short (the five most recent attempts). A row whose
 * answer stored the student's own words (MC justification, the open answer
 * written before the solution, the correction, what was missed) folds them
 * behind "Dina anteckningar" — for the student only (learning-evidence
 * decision #7; teacher views never receive them).
 */
import { useNavigate } from "react-router";
import { SourceBadge, TranslationPendingBadge } from "@client/components/SourceBadge";
import { Badge, Button, Card, Spinner, type BadgeTone } from "@client/components/ui";
import { useLocalizedText } from "@client/components/Localized";
import { useLang, useT } from "@client/i18n";
import { formatPoints, formatRelativeDay } from "@client/lib/format";
import { usePracticeHistory, useTopicsAndChapters } from "./api";
import { createSession, saveSession } from "./session";
import type { HistoryAttempt, HistoryReflection } from "./types";

const VISIBLE = 5;

const REFLECTION_FIELDS: readonly (readonly [keyof HistoryReflection, string])[] = [
  ["justification", "practice.recent.reflection.justification"],
  ["text", "practice.recent.reflection.text"],
  ["correction", "practice.recent.reflection.correction"],
  ["missed", "practice.recent.reflection.missed"],
  ["selfExplanation", "practice.recent.reflection.selfExplanation"],
  ["retrySelfExplanation", "practice.recent.reflection.retrySelfExplanation"],
];

/** The student's own notes on one attempt, folded; plain text, never Markdown. */
function ReflectionNotes({ reflection, topic }: { reflection: HistoryReflection; topic: string }) {
  const t = useT();
  return (
    <details className="w-full text-sm sm:basis-full">
      <summary
        aria-label={t("practice.recent.notesAria", { topic })}
        className="cursor-pointer py-1 text-xs text-ink-600 dark:text-ink-300"
      >
        {t("practice.recent.notes")}
      </summary>
      <dl className="mt-2 space-y-2">
        {REFLECTION_FIELDS.map(([key, labelKey]) => {
          const value = reflection[key];
          if (!value) return null;
          return (
            <div key={key}>
              <dt className="text-xs font-medium text-ink-600 dark:text-ink-300">{t(labelKey)}</dt>
              <dd className="whitespace-pre-wrap break-words text-ink-800 dark:text-ink-100">
                {value}
              </dd>
            </div>
          );
        })}
      </dl>
    </details>
  );
}

/** The outcome badge: a reveal ("Ge upp") is never scored, so it is its own tone. */
function outcome(attempt: HistoryAttempt): { tone: BadgeTone; key: string } {
  if (attempt.kind === "reveal") return { tone: "neutral", key: "practice.summary.gaveUp" };
  if (attempt.correct) return { tone: "success", key: "practice.feedback.correct" };
  // Wrong first, right on the guided retry: the points stay the first try's (pedagogy spec §13 Q12).
  if (attempt.retryCorrect) return { tone: "accent", key: "practice.summary.withHelp" };
  if ((attempt.score ?? 0) > 0) return { tone: "warning", key: "practice.feedback.partial" };
  return { tone: "danger", key: "practice.feedback.incorrect" };
}

export function RecentPractice() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const localize = useLocalizedText();
  const historyQuery = usePracticeHistory(VISIBLE);
  const topicsQuery = useTopicsAndChapters();

  function topicName(attempt: HistoryAttempt): string {
    const topicId = attempt.question?.topic;
    const topic = topicsQuery.data?.topics.find((candidate) => candidate.id === topicId);
    return topic ? localize(topic.name) : (topicId ?? attempt.questionId);
  }

  /** Retry re-serves the exact problem: one question, straight into the loop. */
  function retry(attempt: HistoryAttempt) {
    if (!attempt.question) return;
    saveSession(createSession("topic", [attempt.question]));
    navigate("/practice/session");
  }

  return (
    <Card className="mt-6" title={t("practice.recent.title")}>
      {historyQuery.isLoading && <Spinner size="sm" label={t("common.loading")} />}

      {/* Secondary content: a failed history call says so quietly and leaves the
          setup form — the point of the page — alone. */}
      {historyQuery.isError && (
        <p className="text-sm text-ink-500 dark:text-ink-400">{t("common.error")}</p>
      )}

      {historyQuery.data && historyQuery.data.attempts.length === 0 && (
        <p className="text-sm text-ink-500 dark:text-ink-400">{t("practice.recent.empty")}</p>
      )}

      {historyQuery.data && historyQuery.data.attempts.length > 0 && (
        <ul className="divide-y divide-ink-200 dark:divide-ink-800">
          {historyQuery.data.attempts.slice(0, VISIBLE).map((attempt) => {
            const { tone, key } = outcome(attempt);
            const name = topicName(attempt);
            return (
              // Mobile-first: the topic name gets its own full-width line that
              // wraps rather than an aggressive `truncate` fighting for space
              // with the badges — "Schrödingerekvationen för väte" was
              // shortening to "Schrödin..." at 390px, and rows only grew a
              // second line when a badge happened to be present, so heights
              // were inconsistent down the list. `sm:contents` on the meta
              // wrapper folds it back into one `flex-wrap` row — the original
              // desktop layout — from `sm` up.
              <li
                key={attempt.id}
                className="flex flex-col gap-1 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1"
              >
                <span className="text-sm text-ink-800 dark:text-ink-100 sm:min-w-0 sm:flex-1 sm:truncate">
                  {name}
                </span>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:contents">
                  <SourceBadge source={attempt.question?.source} />
                  <TranslationPendingBadge pending={attempt.question?.translationPending} />
                  <Badge tone={tone}>{t(key)}</Badge>
                  {attempt.kind === "answer" && attempt.maxScore !== null && (
                    <span className="text-xs tabular-nums text-ink-600 dark:text-ink-300">
                      {formatPoints(attempt.score ?? 0, lang)} /{" "}
                      {formatPoints(attempt.maxScore, lang)}
                    </span>
                  )}
                  <span className="text-xs text-ink-500 dark:text-ink-400">
                    {formatRelativeDay(attempt.createdAt, lang)}
                  </span>
                  {attempt.question && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t("practice.recent.retryAria", { topic: name })}
                      onClick={() => retry(attempt)}
                    >
                      {t("practice.recent.retry")}
                    </Button>
                  )}
                </div>
                {attempt.reflection && (
                  <ReflectionNotes reflection={attempt.reflection} topic={name} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default RecentPractice;
